<template>
  <v-main role="presentation" class="white_background">
    <v-container class="widthForContent text-center">
      <v-row v-if="isProcessing" class="centerProgress">
        <v-progress-circular indeterminate :size="size" :width="width" :color="color" />
      </v-row>
      <v-row>
        <v-col cols="12" class="text-center mt-4 mb-4 pb-4">
          <v-img src="@/assets/primerica-logo.svg" height="70" alt="Primerica Beta" />
        </v-col>
        <v-col cols="12" class="text-center">
          <v-row justify="center">
            <v-img
              v-if="isTouchId"
              height="82"
              width="82"
              src="@/assets/TouchID.svg"
              class="mr-2"
              alt="Touch Id"
            />
            <v-img
              v-else-if="isFaceId"
              height="82"
              width="82"
              src="@/assets/FaceID.svg"
              class="mr-2"
              alt="face id"
            />
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="text-center my-4 font-weight-bold">
          <h3>{{ $t('L_want_a_faster_way_to_log_in') }}</h3>
        </v-col>
        <v-col v-if="isTouchId" cols="12" class="text-left text-body-1">
          {{ $t('L_turn_on_touch_or_fingerprint_id_so_you_can_log_in_quickly') }}
        </v-col>
        <v-col v-else-if="isFaceId" cols="12" class="text-left text-body-1">
          {{ $t('L_turn_on_face_id_so_you_can_log_in_with_a_simple') }}
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" text-weight-regular class="text-left text-body-1">
          <span v-if="isTouchId">
            {{ $t('L_sharing_your_device_with_anyone_touch_id') }}
          </span>
          <span v-else-if="isFaceId">
            {{ $t('L_sharing_your_device_with_anyone_face_id') }}
          </span>
        </v-col>
      </v-row>
      <v-row class="pb-12 mb-12 text-center">
        <v-col cols="12" class="my-4">
          <OnexButton
            v-if="isTouchId"
            block
            class="text-weight-medium"
            color="primary"
            @click="startTouchID"
            :class="isFrenchCanadian ? 'text-caption' : 'text-h3'"
          >
            {{ $t('L_use_fingerprint_id') }}
          </OnexButton>
          <OnexButton
            v-else-if="isFaceId"
            block
            class="text-weight-medium"
            color="primary"
            @click="startFaceID"
            :class="isFrenchCanadian ? 'text-caption' : 'text-h3'"
          >
            {{ $t('L_use_face_id') }}
          </OnexButton>
        </v-col>
        <v-col cols="12">
          <OnexButton
            block
            class="text-weight-medium"
            color="white"
            @click="skip"
            :class="isFrenchCanadian ? 'text-caption' : 'text-h3'"
          >
            {{ $t('L_skip') }}
          </OnexButton>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { LOCALES } from '@/shared/models/constants.models';
import {
  hasFaceId,
  hasTouchId,
  setUnlockModeToBiometrics,
  setUnlockModeToSecureStorage,
} from '@/plugins/auth/utils/sessionManager';
import { RouteNames } from '@/router/routeNames';
import { useGlobalStore } from '@/stores/global/global.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { mapStores } from 'pinia';
import { OnexButton } from '@primerica/onex-ui-lib';

const componenOptions: ComponentOptions = {
  name: 'BiometricsPrompt',
  components: {
    OnexButton,
  },
  props: {
    color: {
      type: String,
      default: '#063562',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    size: {
      type: Number,
      default: 70,
    },
    width: {
      type: Number,
      default: 7,
    },
  },
  data: () => ({
    deviceType: null,
    languagesPrefs: window.appConfig.languagePreferences,
    languages: [] as LOCALES[],
    isProcessing: true,
    isFaceId: false,
    isTouchId: false,
    canUseFaceId: false,
    canUseTouchId: false,
    biometricsStrengthStrong: false,
  }),
  computed: {
    ...mapStores(useGlobalStore, useCustomerStore),
    isFrenchCanadian() {
      return this.globalStore.getLocale === LOCALES.fr_CA || this.globalStore.getLocale === 'fr-CA';
    },
  },
  methods: {
    async startFaceID() {
      if (!this.isProcessing) {
        this.isProcessing = true;
        window.setTimeout(() => {
          this.isProcessing = false;
        }, 5000);
        try {
          await setUnlockModeToBiometrics();
          this.isProcessing = false;
          await this.$router.push({ name: RouteNames.SUCCESS_BIOMETRICS });
          // await this.goToNextFlow();
        } catch (e) {
          this.isProcessing = false;
          try {
            DynatraceClient.reportCustomError(
              'PC2_MOBILE_ERR',
              `[BiometricsSplash] ${JSON.stringify(e)}`
            );
            await this.globalStore.postAuditLog({
              actionId: 'MobileApp_Device_FailureAction', // optional
              details: `[${Date.now()}] ERROR CODE ${JSON.stringify(e)}`, // Additional message stored - optional
              message: JSON.stringify(e), // Error type - optional
            });
          } catch (_error) {
            /* empty */
          }
          await this.$router.push({ name: RouteNames.ERROR_BIOMETRICS });
        }
      }
    },
    async startTouchID() {
      if (!this.isProcessing) {
        this.isProcessing = true;
        window.setTimeout(() => {
          this.isProcessing = false;
        }, 5000);
        try {
          await setUnlockModeToBiometrics();
          this.isProcessing = false;
          await this.$router.push({ name: RouteNames.SUCCESS_BIOMETRICS });
          // await this.goToNextFlow();
        } catch (e) {
          this.isProcessing = false;
          try {
            DynatraceClient.reportCustomError(
              'PC2_MOBILE_ERR',
              `[BiometricsSplash] ${JSON.stringify(e)}`
            );
            await this.globalStore.postAuditLog({
              actionId: 'MobileApp_Device_FailureAction', // optional
              details: `[${Date.now()}] ERROR CODE ${JSON.stringify(e)}`, // Additional message stored - optional
              message: JSON.stringify(e), // Error type - optional
            });
          } catch (_error) {
            /* empty */
          }
          await this.$router.push({ name: RouteNames.ERROR_BIOMETRICS });
        }
      }
    },
    async skip() {
      if (!this.isProcessing) {
        this.isProcessing = true;
        window.setTimeout(() => {
          this.isProcessing = false;
        }, 5000);
        try {
          await setUnlockModeToSecureStorage();
          await this.$router.push({ name: RouteNames.ERROR_BIOMETRICS });
        } catch (e) {
          try {
            DynatraceClient.reportCustomError(
              'PC2_MOBILE_ERR',
              `[BiometricsSplash] ${JSON.stringify(e)}`
            );
            await this.globalStore.postAuditLog({
              actionId: 'MobileApp_Device_FailureAction', // optional
              details: `[${Date.now()}] ERROR CODE ${JSON.stringify(e)}`, // Additional message stored - optional
              message: JSON.stringify(e), // Error type - optional
            });
          } catch (_error) {
            /* empty */
          }
          await this.$router.push({ name: RouteNames.ERROR_BIOMETRICS });
        }
        this.isProcessing = false;
      }
    },
    goToNextFlow() {
      const deeplinkroute = window.localStorage.getItem('deeplinkroute'); // ? this is a path, NOT name of a route
      let nextRoute;
      if (this.globalStore.hasTermsOfUseTask) nextRoute = RouteNames.TERMS_OF_USE;
      else if (this.globalStore.hasPolicyAckTask) nextRoute = RouteNames.POLICY_ACKNOWLEDGEMENT;
      else {
        window.localStorage.removeItem('deeplinkroute'); // remove the deeplink route
        nextRoute = deeplinkroute ?? RouteNames.DASHBOARD; // there are no post login tasks
      }
      this.$router.push(deeplinkroute ? { path: nextRoute } : { name: nextRoute });
    },
    updateLocale(language: string) {
      this.globalStore.updateLocale(language as LOCALES);
    },
  },
  async created() {
    await Promise.all([hasFaceId(), hasTouchId()]).then(([_hasFaceId, _hasTouchId]) => {
      this.isFaceId = _hasFaceId;
      this.isTouchId = _hasTouchId;
    });
    this.isProcessing = false;
  },
};

export default defineComponent(componenOptions);
</script>

<style lang="scss" scoped>
.background {
  min-height: 100%;
  background-image: linear-gradient(to top, #607c97, #063562);
}

.white_background {
  min-height: 100%;
  background-color: white;
}

.css_locale {
  background-color: white;
}

.css_app_update {
  font-size: 20px;
  font-weight: bold;
}

.text-body-1 {
  font-size: 20px;
}

.language-icon {
  color: white !important;
}

.container {
  max-width: 960px !important;
}

.widthForContent {
  max-width: 600px !important;
  margin: auto;
}

.btn_white {
  background-color: white !important;
}

.centerProgress {
  left: calc(50% + -38px);
  top: 50vh;
  position: absolute;
}
</style>
